import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Capacitor } from '@capacitor/core';
import V2Providers from './V2Providers';
import V2Routes from './V2Routes';
import V2UpdateOverlay from '../components/V2UpdateOverlay';
import { runV2BootUpdates } from '../platform/updater';
import {
  registerAndGetToken,
  setupPushTokenRefreshListener,
} from '../platform/notifications';
import { injectSafeAreaVars } from '../platform/safeArea';
import { registerBackHandler, exitApp } from '../platform/navigation';
import { tryMigrateLegacySession } from '../services/sessionMigration';
import {
  ensurePushTokenSynced,
  onFcmTokenReady,
  resolveMobileFromSession,
  setupForegroundPushSync,
  syncGuestNeptuneOnFirstOpen,
} from '../services/pushSync';
import { getCachedSession } from '../services/user';
import { trackGa4AppOpen } from '../services/firebaseAnalytics';
import { v2Theme } from '../design/theme';
import { v2MuiTheme } from '../design/muiTheme';

function useV2Boot() {
  const [ready, setReady] = useState(false);
  const [initialSession, setInitialSession] = useState(null);
  const [updatePercent, setUpdatePercent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let removeForegroundSync = () => {};

    setupPushTokenRefreshListener(onFcmTokenReady);

    setupForegroundPushSync().then((remove) => {
      if (!cancelled) removeForegroundSync = remove;
    });

    async function boot() {
      await runV2BootUpdates((p) => {
        if (!cancelled) setUpdatePercent(p);
      });
      if (cancelled) return;

      injectSafeAreaVars();
      let session = await getCachedSession();
      if (!session?.token) {
        const migrated = await tryMigrateLegacySession();
        if (migrated) session = migrated;
      }

      if (!cancelled) {
        setUpdatePercent(null);
        setInitialSession(session);
        setReady(true);
      }

      const bootMobile = resolveMobileFromSession(session);
      if (bootMobile.length === 10) {
        ensurePushTokenSynced({ mobile: bootMobile });
      } else {
        syncGuestNeptuneOnFirstOpen();
      }

      registerAndGetToken().then((token) => {
        if (!token) return;
        onFcmTokenReady(token, bootMobile.length === 10 ? bootMobile : undefined);
      });
    }

    boot();
    return () => {
      cancelled = true;
      removeForegroundSync();
    };
  }, []);

  return { ready, initialSession, updatePercent };
}

function V2AppInner() {
  const { ready, initialSession, updatePercent } = useV2Boot();

  const backRef = useRef(null);
  useEffect(() => {
    backRef.current = registerBackHandler(({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else exitApp();
    });
    return () => backRef.current?.();
  }, []);

  useEffect(() => {
    if (!ready) return;
    trackGa4AppOpen({ platform: Capacitor.getPlatform() });
  }, [ready]);

  if (updatePercent != null) {
    return <V2UpdateOverlay percent={updatePercent} />;
  }

  if (!ready) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ bgcolor: v2Theme.palette.background }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <V2Providers initialSession={initialSession}>
      <V2Routes />
    </V2Providers>
  );
}

export default function V2App() {
  return (
    <ThemeProvider theme={v2MuiTheme}>
      <V2AppInner />
    </ThemeProvider>
  );
}
